import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import MonthTable from "./MonthTable";
import {
  GridActionsCellItem,
  GridColDef,
  gridFilteredSortedRowIdsSelector,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowClassNameParams,
  useGridApiRef,
} from "@mui/x-data-grid";
import { AppDataGrid } from "../../../../components/datagrid/AppDataGrid";
import { Trans, useTranslation } from "react-i18next";
import { tableStyle } from "../data-table/EmissionDataTableStyle";
import TablePopper from "../../../../components/popper/TablePopper";
import NumberFormatterUtils from "../../../../utils/numberFormatterUtils";
import { QuickUploadFile } from "../../../../components/input/QuickUploadFile";
import { DataEntryRoleContext } from "../../../../contexts/DataEntryRoleContext";
import { Activity, GetActivities } from "../../../../models/Activities";
import { useForm } from "react-hook-form";
import { DROPDOWN_EMISSION_GROUP } from "../../../../components/emission/emission-components";
import { DisabledInput } from "../data-table/DisabledInput";
import alertService from "../../../../components/alert/alertService";
import ActivitiesService from "../../../../service/activities.service";
import { EditEmissionDateEntryRole } from "./EditEmissionDateEntryRole";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../../contexts/UserContext";
import { MultipleAutoComplete } from "../../../../components/input/MutipleAutoComplete";
import ArrayUtils from "../../../../utils/arrayUtils";
import { MenuData } from "../../../../components/input/CustomSelect";
import AddEmissionForm from "../popup-add-emission/PopupAddEmission";
import { AddEmissionContext } from "../../../../contexts/AddEmissionContext";
import ScopeSummary from "../ScopeSummary";
import { ReviewApproval } from "../data-table/ReviewApproval";
import TableApprovalStatus from "../data-table/TableApprovalStatus";
import dayjs, { Dayjs } from "dayjs";
import { QuickSearchToolbar } from "./QuickSearchToolbar";
import { EditEmission } from "../data-table/EditEmission";
import { common, input } from "../../../../i18n";
import CustomDatePicker from "../../../../components/input/CustomDatePicker";
import { PaginationWithRowSelected } from "../../../../components/datagrid/PaginationWithRowSelected";
import { NumericField } from "../../../../components/input/NumericField";
import { isScope3Open } from "../../../../config/config";

const AmountCell = ({
  params,
  control,
  setValue,
  getValues,
  setFocus,
  inputRefs,
  inputKeyEventRef,
  handleUpdateAmount,
  autoFocusNextInput,
  t,
}: {
  params: GridRenderCellParams;
  control: any;
  setValue: any;
  getValues: any;
  setFocus: any;
  inputRefs: any;
  inputKeyEventRef: any;
  handleUpdateAmount: (row: Activity) => Promise<void>;
  autoFocusNextInput: (id: number) => void;
  t: any;
}) => {
  const originalRow = params.row as Activity;
  const inputId = `input-amount-${params.id}`;

  // ✅ ใช้ useEffect เพื่อ Set Value ให้ Form
  useEffect(() => {
    const currentValue = getValues(inputId);

    // ✅ ตัดหรือปัดเศษให้เหลือสูงสุด 4 ตำแหน่ง (ถ้าเป็นเลขจำนวนเต็มจะไม่มี .0000 ต่อท้าย)
    const rawValue = params.value ?? 0;

    const gridValue = Number(Number(rawValue).toFixed(4));

    // อัปเดตเฉพาะเมื่อค่าไม่ตรงกัน เพื่อป้องกัน Loop
    if (currentValue !== gridValue) {
      setValue(inputId, gridValue);
    }
  }, [inputId, params.value, setValue, getValues]);

  const item = DROPDOWN_EMISSION_GROUP.find(
    (e) => e.emissionGroup === params.row.emissionGroup,
  );
  const canQuickUpdateAmount = item?.canQuickUpdateAmount ?? true;
  const disabled: boolean =
    originalRow.status === "approved" || !canQuickUpdateAmount;

  if (!canQuickUpdateAmount) {
    // ถ้ารับค่าทศนิยม 4 ตำแหน่งไปแสดงผลใน DisabledInput ด้วย
    // อาจจะต้องครอบ Number(Number(params.value).toFixed(4)) ตรงนี้ด้วยถ้าต้องการให้แสดงผลตรงกัน
    return <DisabledInput inputName={inputId} value={params.value} />;
  }

  return (
    <NumericField
      showLabelInput={false}
      id={inputId}
      size="small"
      key={inputId}
      name={inputId}
      control={control}
      allowNegative={false}
      decimalScale={4}
      inputRef={(el) => {
        if (inputRefs.current) {
          inputRefs.current[String(originalRow.id)] = el;
        }
      }}
      onBlur={async () => {
        await handleUpdateAmount(originalRow);
        if (inputKeyEventRef.current) {
          setTimeout(() => autoFocusNextInput(originalRow.id), 100);
        }
      }}
      onKeyDown={(e) => e.stopPropagation()}
      onKeyUp={(e) => {
        if (["Enter", "ArrowUp", "ArrowDown"].includes(e.key)) {
          inputKeyEventRef.current = e.key as any;
          (document.activeElement as HTMLElement)?.blur();
        }
      }}
      disabled={disabled}
      onFocus={(event) => {
        requestAnimationFrame(() => {
          (event.target as HTMLInputElement)?.select?.();
        });
      }}
      inputProps={{
        id: `input-amount-${originalRow.id}`,
        inputMode: "decimal",
        autoComplete: "off",
      }}
    />
  );
};

export default AmountCell;

export const DataEntryRole = () => {
  const {
    year,
    activities,
    getActivitiesList,
    setUpActivities,
    setFilterMonth,
    filterByMonthlyTemplate,
    setFilterByMonthlyTemplate,
    updateActivitiesAfterEdit,
    setYear,
    selectionModel,
    setSelectionModel,
    rows,
    setRows,
    setUpSumScopes,
    setSuborganizationRefs,
  } = DataEntryRoleContext();

  const { subOrganizationList } = AddEmissionContext();

  const { isDataEntry, isAdmin } = useAuth();

  const { t, i18n } = useTranslation([common, input], {
    nsMode: "fallback",
  });

  const apiRef = useGridApiRef();

  const { setValue, getValues, setFocus, control, watch } = useForm<any>();

  const inputKeyEventRef = useRef<"" | "Enter" | "ArrowUp" | "ArrowDown">("");

  // เก็บ ref ของ input ของแต่ละแถว
  const inputRefs = useRef<Record<string | number, HTMLInputElement | null>>(
    {},
  );

  // popper
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openPopper = Boolean(anchorEl);
  const [rowSelect, setRowSelect] = useState<Activity | null>(null);
  const [openReviewApproval, setOpenReviewApproval] = useState(false);
  const [openReviewApprovalType, setOpenReviewApprovalType] = useState("");
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [editItem, setEditItem] = useState<Activity | undefined>();

  const checkedAll = () => {
    if (selectionModel.length === 0) return false;
    const currentDisplayItem = getCurrentDisplayItem();
    return currentDisplayItem.every((e) => selectionModel.includes(e));
  };

  const indeterminateAll = () => {
    const currentDisplayItem = getCurrentDisplayItem();
    return (
      currentDisplayItem.some((e) => selectionModel.includes(e)) &&
      !checkedAll()
    );
  };

  const columns: GridColDef[] = [
    {
      field: "select",
      headerAlign: "center",
      renderHeader: () => {
        return (
          <Checkbox
            id={`checkbox-all`}
            checked={checkedAll()}
            indeterminate={indeterminateAll()}
            onChange={() => handleCheckboxAll()}
            sx={{
              display: filterByMonthlyTemplate ? "none" : "block",
            }}
          />
        );
      },
      width: 70,
      sortable: false,
      filterable: false,
      align: "center",
      renderCell: (params) => (
        <Checkbox
          id={`checkbox-${params.row._id}`}
          checked={selectionModel.includes(params.row.id)}
          onChange={() => handleCheckboxChange(params.row.id)}
          sx={{
            display: filterByMonthlyTemplate ? "none" : "block",
          }}
        />
      ),
    },
    {
      field: "id",
      headerName: t("ID"),
      disableColumnMenu: true,
      minWidth: 80,
      type: "string",
      headerAlign: "left",
      align: "left",
      valueFormatter: (value) => `EM-${value}`,
    },
    {
      field: "startDate",
      headerName: t("Date"),
      width: 100,
      disableColumnMenu: true,
      headerAlign: "left",
      type: "date",
      valueFormatter: (value: string, row) => {
        const start: Dayjs = dayjs(value.substring(0, 10));
        const end: Dayjs = dayjs(row.endDate.substring(0, 10));
        if (start.isSame(end, "month")) {
          return start.locale(i18n.language).format("MMM YYYY");
        } else {
          return `${start.locale(i18n.language).format("MMM YYYY")} -\n${end
            .locale(i18n.language)
            .format("MMM YYYY")}`;
        }
      },
      renderCell: (params) => {
        return (
          <Typography variant="text1" sx={{ whiteSpace: "pre-line" }}>
            {params.formattedValue}
          </Typography>
        );
      },
    },
    {
      field: "suborganizationName",
      headerName: t("Sub-Organizations"),
      headerAlign: "left",
      minWidth: 100,
      flex: 1,
      disableColumnMenu: true,
    },
    {
      field: "assetName",
      headerName: t("Asset"),
      minWidth: 120,
      disableColumnMenu: true,
      flex: 1,
    },
    {
      field: "emissionGroup",
      headerName: t("Emission-Group"),
      width: 134,
      headerAlign: "left",
      disableColumnMenu: true,
      valueFormatter: (value) => {
        const item = DROPDOWN_EMISSION_GROUP.find(
          (e) => e.emissionGroup === value,
        );
        return item ? t(item.groupEn) : t(value);
      },
    },
    {
      field: "activityAttributeEN",
      headerName: t("Emission-Source"),
      minWidth: 160,
      flex: 1,
      headerAlign: "left",
      disableColumnMenu: true,
      valueFormatter: (value, row) =>
        i18n.language === "en"
          ? row.activityAttributeEN
          : row.activityAttributeTh,
    },
    {
      field: "amount",
      headerName: t("Amount"),
      headerAlign: "right",
      align: "right",
      minWidth: 170,
      sortable: false,
      disableColumnMenu: true,
      editable: false,
      type: "number",
      flex: 1,
      renderCell: (params) => (
        <AmountCell
          params={params}
          control={control}
          setValue={setValue}
          getValues={getValues}
          setFocus={setFocus}
          inputRefs={inputRefs}
          inputKeyEventRef={inputKeyEventRef}
          handleUpdateAmount={handleUpdateAmount}
          autoFocusNextInput={autoFocusNextInput}
          t={t}
        />
      ),
    },
    {
      field: "unit",
      headerName: t("Unit"),
      minWidth: 50,
      disableColumnMenu: true,
    },
    {
      field: "carbonEquivalentValue",
      sortable: true,
      minWidth: 150,
      type: "number",
      headerAlign: "right",
      flex: 1,
      disableColumnMenu: true,
      getApplyQuickFilterFn: undefined,
      headerName: `${t("Emission")} (kgCO₂e)`,
      renderCell: (params) => {
        let result: number = 0;
        try {
          result = params.row.scopes.s1 + params.row.scopes.s2;
          if (isScope3Open) {
            result =
              params.row.scopes.s1 +
              params.row.scopes.s2 +
              params.row.scopes.s3;
          }
        } catch {
          result = 0;
        }
        return (
          <Box alignItems={"right"}>
            <TablePopper
              text={NumberFormatterUtils.numberFormat(result)}
              scope={params.row.scopes}
              unit={"kg"}
            />
          </Box>
        );
      },
    },
    {
      field: "attachment",
      headerName: t("Files"),
      sortable: false,
      width: 80,
      align: "center",
      headerAlign: "center",
      getApplyQuickFilterFn: undefined,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<Activity>) => (
        <>
          <QuickUploadFile
            filename={params.value ?? ""}
            id={params.row._id}
            attachmentRef={params.row.attachmentRef}
            quickUploadFile={(base64: string) =>
              quickUploadFileV1(base64, params.row._id)
            }
          />
        </>
      ),
    },
    {
      field: "status",
      headerName: t("Status"),
      disableColumnMenu: true,
      width: 100,
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <TableApprovalStatus
            status={params.value}
            by={
              params.value === "approved"
                ? params.row.approvedBy
                : params.row.lastUpdatedBy ?? params.row.createdBy
            }
          />
        );
      },
    },
    {
      field: "remark",
      headerName: t("Remark"),
      sortable: false,
      minWidth: 137,
    },
    {
      field: "",
      headerName: "",
      disableColumnMenu: true,
      sortable: false,
      type: "actions",
      width: 50,
      getApplyQuickFilterFn: undefined,
      getActions: (params) => {
        // ของเดิม isDataEntry จะได้แค่ปุ่ม Edit แต่พอย้ายไปกดที่ Row แล้ว
        // จึงไม่ต้องแสดงเมนู Action อะไรเลยสำหรับ Role นี้
        const isEntry =
          typeof isDataEntry === "function" ? isDataEntry() : isDataEntry;
        if (isEntry) {
          return [];
        }

        return [
          <GridActionsCellItem
            key={`${params.row.id}-more`}
            icon={<img src="/img/more.svg" alt="icon-more" />}
            label="more"
            id="basic-button"
            aria-controls={openPopper ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={openPopper ? "true" : undefined}
            onClick={(event) => handleOnClickPopper(event, params.row)}
          />,
        ];
      },
    },
  ];

  const [subOrganizations, setSubOrganizations] = useState<MenuData[]>([]);

  // Watch ค่าจาก Dropdown (เป็น refId ของ Org)
  const subOrganization = watch("subOrganization");

  useEffect(() => {
    setSuborganizationRefs(subOrganization || []);
  }, [subOrganization, setSuborganizationRefs]);

  const editActivitiesAmount = async (updatedRow: Activity) => {
    const isConfirmed = await alertService.confirm(
      t("Edit-Emission"),
      <Trans
        t={t}
        i18nKey="Edit-Emission-Model-text"
        values={{ id: updatedRow.id }}
        components={{
          span: <span style={{ color: "var(--red)" }} />,
        }}
      />,
      t("Save"),
    );

    if (isConfirmed) {
      let editResult: GetActivities | undefined;
      try {
        editResult = await ActivitiesService.updateAmount(
          updatedRow._id,
          updatedRow.amount,
          year,
        );
      } catch {
        // error alert already shown by service
        return false;
      }

      if (!editResult) {
        return false;
      }

      await getActivitiesList();
      alertService.mixin(t("edit-emission-amount-successfully"));
      return true;
    }

    return false;
  };

  const handleUpdateAmount = async (originalRow: Activity) => {
    await new Promise((resolve) => setTimeout(resolve, 50));

    const inputName = `input-amount-${originalRow.id}`;
    const newAmount: string = `${getValues(inputName)}`;
    let newAmountNumber: number = Number(newAmount.replaceAll(",", ""));

    if (newAmountNumber === originalRow.amount) {
      setValue(inputName, originalRow.amount);
      return;
    }

    if (Number.isNaN(newAmountNumber)) {
      await alertService.error(t("only-numbers-are-allowed"));
      // ✅ Reset .current
      inputKeyEventRef.current = "";
      setTimeout(() => {
        setValue(inputName, originalRow.amount);
        setFocus(inputName);
      }, 50);
      return;
    }

    if (newAmountNumber < 0) {
      await alertService.error(t("can't_be_less_than_0"));
      // ✅ Reset .current
      inputKeyEventRef.current = "";
      setTimeout(() => {
        setValue(inputName, originalRow.amount);
        setFocus(inputName);
      }, 50);
      return;
    }

    const updatedRow = { ...originalRow, amount: newAmountNumber };
    const result = await editActivitiesAmount(updatedRow);
    if (!result) {
      setValue(inputName, originalRow.amount);
    }
  };

  const autoFocusNextInput = (currentId: number) => {
    const allRow = gridFilteredSortedRowIdsSelector(apiRef);
    const currentIndex = allRow.findIndex((e) => e === currentId);

    let nextIndex = -1;
    // ✅ ใช้ .current ในการเช็ค logic
    switch (inputKeyEventRef.current) {
      case "Enter":
      case "ArrowDown":
        nextIndex = currentIndex + 1;
        break;
      case "ArrowUp":
        nextIndex = currentIndex - 1;
        break;
      default:
        nextIndex = -1;
        break;
    }

    // ✅ Reset .current
    inputKeyEventRef.current = "";

    if (nextIndex < 0 || nextIndex >= allRow.length) return;

    const nextId = allRow[nextIndex];

    try {
      apiRef.current.scrollToIndexes?.({ rowIndex: nextIndex });
    } catch {
      /* noop */
    }

    requestAnimationFrame(() => {
      setTimeout(() => {
        const el =
          inputRefs.current[String(nextId)] ||
          (apiRef.current
            .getCellElement(nextId, "amount")
            ?.querySelector("input") as HTMLInputElement | null);

        if (el) {
          el.focus();
          el.select?.();
        } else {
          const nextInputName = `input-amount-${nextId}`;
          try {
            setFocus(nextInputName);
          } catch {
            // ignore
          }
        }
      }, 60);
    });
  };

  const quickUploadFileV1 = async (base64: string, refId: string) => {
    let result;
    try {
      result = await ActivitiesService.updateAttachment(refId, base64, year);
    } catch {
      // error alert already shown by service
      return;
    }

    if (result) {
      await updateActivitiesAfterEdit();
      alertService.mixin(t("Upload-Evidence-Success"));
    }
  };

  const handleOnClickPopper = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: any,
  ) => {
    setRowSelect(row);
    setAnchorEl(event.currentTarget);
  };

  const deleteActivities = async () => {
    if (rowSelect) {
      const isConfirmed = await alertService.confirm(
        t("Delete-Emission"),
        <Trans
          t={t}
          i18nKey="Confirm-Delete-Emission-With-ID"
          values={{ id: rowSelect.id }}
          components={{
            span: <span style={{ color: "var(--red)" }} />,
          }}
        />,
        t("Delete"),
        "var(--red)",
      );

      if (isConfirmed) {
        let result;
        try {
          result = await ActivitiesService.softDelete(rowSelect._id, year);
        } catch {
          // error alert already shown by service
          return;
        }
        if (result) {
          alertService.mixin(t("delete-emission-successfully"));
          setUpActivities(result.activities);
          setUpSumScopes(result.sumScopes);
        }
      }
    }
  };

  const handleClose = async (event?: any) => {
    setAnchorEl(null);
    if (event?.target?.id === "review-approval") {
      setOpenReviewApproval(true);
      setOpenReviewApprovalType("approval");
    } else if (event?.target?.id === "view") {
      setOpenReviewApproval(true);
      setOpenReviewApprovalType("view");
    } else if (event?.target?.id === "deleted") {
      deleteActivities();
    }
  };

  useEffect(() => {
    const temp = ArrayUtils.convertArrayToMenuData(
      "refId",
      "name",
      "name",
      subOrganizationList,
    );
    setSubOrganizations(temp);
    // เลือกทั้งหมดเป็นค่าเริ่มต้น
    setValue("subOrganization", [...temp.map((e) => e.value)]);
  }, [subOrganizationList]);

  // ตั้งค่า rows ตาม activities จาก context (API เป็นคน Filter ให้แล้ว)
  useEffect(() => {
    setSelectionModel([]);
    setRows(activities);
  }, [activities]);

  const chageFilterDateType = async (event: any) => {
    const filterByMonthlyTemplate =
      event.target.value === "true" ? true : false;

    if (filterByMonthlyTemplate) {
      setFilterMonth([]);

      if (apiRef.current) {
        apiRef.current.setFilterModel({
          items: [],
          quickFilterValues: [],
        });
      }
    }

    setTimeout(() => {
      setFilterByMonthlyTemplate(filterByMonthlyTemplate);
    }, 100);
  };

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 50,
    page: 0,
  });

  const getCurrentDisplayItem = () => {
    const firstIndex = paginationModel.page * paginationModel.pageSize;
    const lastIndex = (paginationModel.page + 1) * paginationModel.pageSize;
    const currentDisplayItem = [
      ...Array.from(apiRef.current.getRowModels().keys()).slice(
        firstIndex,
        lastIndex,
      ),
    ];
    return currentDisplayItem;
  };

  const handleCheckboxAll = () => {
    const currentDisplayItem = getCurrentDisplayItem();

    if (indeterminateAll()) {
      return setSelectionModel([
        ...selectionModel.filter((e) => !currentDisplayItem.includes(e)),
      ]);
    }

    if (currentDisplayItem.every((e) => selectionModel.includes(e))) {
      return setSelectionModel(
        [...selectionModel, ...currentDisplayItem].filter(
          (e) => !currentDisplayItem.includes(e),
        ),
      );
    } else {
      return setSelectionModel([
        ...selectionModel.filter((e) => !currentDisplayItem.includes(e)),
        ...currentDisplayItem,
      ]);
    }
  };

  const handleCheckboxChange = (id: number) => {
    if (selectionModel.includes(id)) {
      setSelectionModel([...selectionModel, id].filter((e) => e !== id));
    } else {
      setSelectionModel([...selectionModel, id]);
    }
  };

  return (
    <>
      {rowSelect ? (
        <ReviewApproval
          data={rowSelect}
          subOrganizationList={subOrganizationList}
          open={openReviewApproval}
          onClose={() => {
            setOpenReviewApproval(false);
            setRowSelect(null);
          }}
          type={openReviewApprovalType}
        />
      ) : null}
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={openPopper}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <MenuItem
          disabled={!isAdmin() || rowSelect?.status.toLowerCase() !== "pending"}
          onClick={handleClose}
          id="review-approval"
        >
          {t("Review-Approval")}
        </MenuItem>
        <MenuItem onClick={handleClose} id="view">
          {t("View")}
        </MenuItem>
        <Divider />
        <MenuItem
          id="deleted"
          onClick={handleClose}
          disabled={
            rowSelect?.generatedByTemplate
              ? true
              : rowSelect?.status.toLowerCase() !== "approved"
              ? false
              : isAdmin() && rowSelect?.status.toLowerCase() === "approved"
              ? false
              : true
          }
          sx={{
            color: "var(--red)",
          }}
        >
          {t("Delete")}
        </MenuItem>
      </Menu>
      {!isDataEntry() && (
        <Grid container width={"100%"} gap={"20px"}>
          <Grid item xs={12} sm={12} md={4}>
            {subOrganizations.length > 0 && (
              <MultipleAutoComplete
                showLabelInput={false}
                name={"subOrganization"}
                control={control}
                rules={{}}
                label={t("Sub-Organizations")}
                info={t("Sub-Organizations")}
                placeholder={t("Select-Param", {
                  param: t("Sub-Organizations"),
                })}
                listMenuItem={subOrganizations}
                defaultValue={[]}
                storeValue
              />
            )}
          </Grid>

          <Grid item xs={12} sm={6} md display={"flex"} gap={"20px"}>
            <FormControl
              sx={{
                minWidth: 180,
              }}
              size="small"
            >
              <InputLabel id="demo-simple-select-helper-label">
                {t("Data-Type")}
              </InputLabel>
              <Select
                labelId="demo-simple-select-helper-label"
                id="demo-simple-select-helper"
                label={t("Data-Type")}
                onChange={chageFilterDateType}
                value={filterByMonthlyTemplate === true ? "true" : "false"}
                sx={{
                  height: "40px",
                }}
              >
                <MenuItem value={"false"}>{t("All")}</MenuItem>
                <MenuItem value={"true"}>{t("Monthly-Templates")}</MenuItem>
              </Select>
            </FormControl>

            <Box>
              <CustomDatePicker
                value={dayjs(`${year}-01-01`)}
                defaultDate={dayjs(`${year}-01-01`)}
                label={t("Year")}
                info={t("Year")}
                required
                handleOnChange={(date: Dayjs) => setYear(date.year())}
                views={["year"]}
                showInfo={false}
                format="YYYY"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm md="auto">
            <AddEmissionForm />
          </Grid>
        </Grid>
      )}
      {filterByMonthlyTemplate === false ? <ScopeSummary /> : undefined}
      {filterByMonthlyTemplate && <MonthTable />}
      {editItem && editItem?.generatedByTemplate && (
        <EditEmissionDateEntryRole
          open={openEditModal}
          onClose={() => setOpenEditModal(false)}
          activity={editItem}
        />
      )}
      {editItem && !editItem?.generatedByTemplate && (
        <EditEmission
          data={editItem}
          open={openEditModal}
          onClose={() => {
            setOpenEditModal(false);
            setEditItem(undefined);
          }}
        />
      )}

      {/* ✅ กล่องนอก: ทำหน้าที่เป็น Placeholder จองพื้นที่แบบ relative ให้เต็มพื้นที่ที่เหลือ */}
      <Box
        display="flex"
        flexDirection="column"
        flex={1}
        width="100%"
        position="relative"
        minHeight={{ xs: "75vh", md: 400 }}
      >
        {/* ✅ กล่องใน: ขึง Absolute ทั้ง 4 มุม เพื่อขัง DataGrid ไม่ให้ดัน Layout ทะลุจอ */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            [`.selected`]: {
              backgroundColor: "rgba(11, 148, 133, 0.08)",
            },
          }}
        >
          <AppDataGrid
            apiRef={apiRef}
            rows={rows}
            columns={columns}
            rowHeight={60}
            // ✅ 1. เพิ่ม onCellClick เพื่อดักจับการกดที่ Row
            onCellClick={async (params, event) => {
              // ยกเว้นคอลัมน์ที่ไม่ต้องการให้คลิกแล้วเด้ง Edit (เช่น Checkbox, กรอกตัวเลข, อัปโหลดไฟล์, จุดไข่ปลา และ Popper)
              const ignoreColumns = [
                "select",
                "amount",
                "attachment",
                "",
                "carbonEquivalentValue",
              ];
              if (ignoreColumns.includes(params.field)) {
                return;
              }

              // เงื่อนไขเดิม (จากปุ่ม Edit เก่า)
              // ถ้าสถานะ approved แล้วให้กดไม่ได้
              if (params.row.status === "approved") {
                return;
              }

              // สั่งเปิด Modal Edit
              const temp: Activity = { ...params.row };
              const file = await ActivitiesService.getAttachmentMeta(temp._id);
              if (file) {
                temp.attachment = file.attachment;
              }
              setEditItem(temp);
              setOpenEditModal(true);
            }}
            onCellKeyDown={(params, event) => {
              if (params.field === "amount") {
                event.stopPropagation();
                return;
              }
            }}
            slots={{
              toolbar: QuickSearchToolbar,
              pagination: () => (
                <PaginationWithRowSelected selectionModel={selectionModel} />
              ),
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: {
                  variant: "outlined",
                },
              },
            }}
            pagination
            pageSizeOptions={[25, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sx={{
              ...tableStyle(),
              "& .MuiDataGrid-row.Mui-disabled": {
                cursor: "default",
              },
            }}
            getRowClassName={(params: GridRowClassNameParams) => {
              return selectionModel.includes(params.id) ? "selected" : "";
            }}
            columnVisibilityModel={{
              select: filterByMonthlyTemplate ? false : true,
              remark: !filterByMonthlyTemplate ? false : true,
            }}
          />
        </Box>
      </Box>
    </>
  );
};
